import React, { useState, useEffect } from "react";
import useProfileDashboard from "../../services/Settings/useProfileDashboard";
import usePayout from "../../services/Order/usePayout";
import PaginationComponent from "../../utils/TablePagination";
import Ledger from "../orders/Ledger";
import { toast, ToastContainer } from "react-toastify";
import LoadingScreen from "../../utils/LoadingScreen";

const PayOut = ({ userID }) => {
  const {
    payouts,
    wallet,
    ledger,
    loading,
    getPayoutReport,
    getWalletBalance,
    settlementProcess,
    getLedger,
  } = usePayout();

  const { getBankAccounts } = useProfileDashboard();

  const [activeTab, setActiveTab] = useState("report");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState("");
  const [bankInfo, setBankInfo] = useState([]);
  const [settlementAmount, setSettlementAmount] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const data =
    activeTab === "report" ? payouts || [] : ledger?.response || ledger || [];

  const displayedItems = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const getToday = () => {
    const d = new Date();
    return d.toISOString().split("T")[0];
  };

  const handleSettlement = async () => {
    if (!settlementAmount) {
      toast.error("Please enter settlement amount");
      return;
    }

    const res = await settlementProcess(settlementAmount);

    if (res?.responseCode === "00") {
      toast.success(res.responseMessage || "Settlement successful");

      setShowModal(false);
      setSettlementAmount("");
    } else {
      toast.error(res?.responseMessage || "Settlement failed");
    }
  };

  const validBanks = bankInfo.filter(
    (b) => b.us_account && b.ifsc_code && b.bank_name,
  );

  useEffect(() => {
    const today = getToday();
    setStartDate(today);
    setEndDate(today);

    getWalletBalance(userID);
    getBankAccounts(userID);
  }, []);

  useEffect(() => {
    if (activeTab === "report" && startDate && endDate) {
      getPayoutReport(userID, startDate, endDate);
    }
  }, [activeTab, startDate, endDate, userID]);

  useEffect(() => {
    if (activeTab === "ledger") {
      if (startDate && endDate) {
        getLedger(userID, startDate, endDate);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    const fetchBanks = async () => {
      const res = await getBankAccounts(userID);

      if (res?.responseCode === "00") {
        setBankInfo(res.response || []);
      }
    };

    fetchBanks();
  }, [userID]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, payouts, ledger]);

  return (
    <>
    <ToastContainer/>
      <div className="flex justify-center text-center montserrat-font-1">

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center montserrat-font-1 justify-center bg-black/40">
            <div className="bg-white w-full max-w-2xl montserrat-font-1 rounded-lg shadow-lg">
              <div className="flex justify-between items-center px-6 py-4 border-b">
                <h2 className="text-lg font-bold montserrat-font-1 text-gray-800">Settlement</h2>

                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              <div className="px-6 py-5">
                <div className="border rounded-lg p-5 mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="text-gray-800 font-semibold">
                      Bank Details
                    </h5>
                  </div>

                  <hr className="mb-4" />

                  {validBanks.map((info) => (
                    <div key={info.id} className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-900 font-semibold">
                          Account Number:
                        </span>
                        <span className="font-bold">{info.us_account}</span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-900 font-semibold">
                          IFSC Code:
                        </span>
                        <span className="font-bold">{info.ifsc_code}</span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-900 font-semibold">
                          Bank Name:
                        </span>
                        <span className="font-bold">{info.bank_name}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Settlement Amount */}
                <div className="border rounded-lg bg-gray-50 p-5 mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="text-gray-800 font-semibold">
                      Enter Settlement Amount
                    </h5>
                  </div>

                  <hr className="mb-4" />

                  <div className="relative">
                    <input
                      type="number"
                      placeholder="Enter settlement amount"
                      value={settlementAmount}
                      onChange={(e) => setSettlementAmount(e.target.value)}
                      className="w-full border rounded-lg px-4 py-3 pr-10 focus:outline-none focus:ring-2 focus:ring-green-400"
                    />

                    <span className="absolute right-3 top-3 text-green-500 font-bold">
                      ₹
                    </span>
                  </div>
                </div>

                {/* Loader */}
                {loading && (
                  <div className="flex justify-center py-3">
                       <LoadingScreen text="Loading..." />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 px-6 py-4 border-t">
                <button
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                  className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
                >
                  Close
                </button>

                <button
                  onClick={handleSettlement}
                  disabled={!settlementAmount || loading}
                  className="px-4 py-2 rounded-md bg-green-500 text-white hover:bg-green-600 disabled:opacity-50"
                >
                  {loading ? "Processing..." : "Proceed"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="p-6 bg-white rounded-lg shadow montserrat-font-1">
        {/* Tabs */}
        <div className="mb-6 montserrat-font-1">
          {/* Header */}
          <h2 className="font-semibold text-lg mb-4">
            {activeTab === "report" ? "PayOut Report" : "Account Ledger"}
          </h2>

          {/* Tabs */}
          <div className="flex border-b montserrat-font-1">
            <button
              onClick={() => setActiveTab("report")}
              className={`px-5 py-2 text-sm font-medium transition ${
                activeTab === "report"
                  ? "border-b-2 border-green-500 text-green-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Report
            </button>

            <button
              onClick={() => setActiveTab("ledger")}
              className={`px-5 py-2 text-sm font-medium transition ${
                activeTab === "ledger"
                  ? "border-b-2 border-green-500 text-green-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Ledger
            </button>
          </div>
        </div>

        {/* Top Controls */}
        <div className="flex flex-wrap justify-end gap-6 mb-6 montserrat-font-1">
          {/* Wallet Balance */}
          <div className="flex flex-col text-sm">
            <label className="text-gray-500 mb-1">Wallet Balance</label>
            <div className="bg-gray-100 px-4 py-2 rounded font-semibold text-green-600">
              ₹ {parseFloat(wallet.wallet_bal || 0).toFixed(2)}
            </div>
          </div>

          {/* Unsettled */}
          <div className="flex flex-col text-sm">
            <label className="text-gray-500 mb-1">Unsettled Amount</label>
            <div className="bg-gray-100 px-4 py-2 rounded font-semibold text-green-600">
              ₹ {parseFloat(wallet.unsettled_Amount || 0).toFixed(2)}
            </div>
          </div>

          {/* Start Date */}
          <div className="flex flex-col text-sm">
            <label className="text-gray-500 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border rounded px-3 py-2"
            />
          </div>

          {/* End Date */}
          <div className="flex flex-col text-sm">
            <label className="text-gray-500 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border rounded px-3 py-2"
            />
          </div>

          {/* Settlement Button */}
          <div className="flex flex-col text-sm justify-end">
            <label className="text-transparent mb-1">Action</label>
            <button
              onClick={() => setShowModal(true)}
              className="bg-green-500 text-white px-5 py-2 rounded hover:bg-green-600 transition"
            >
              Settlement
            </button>
          </div>
        </div>

        {/* Ledger */}
        {activeTab === "ledger" ? (
          <Ledger
            rows={displayedItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
          />
        ) : (
          <div className="overflow-auto montserrat-font-1">
            <table className="min-w-full border montserrat-font-1">
              <thead className="bg-green-500 text-white montserrat-font-1">
                <tr>
                  <th className="p-3">S.No</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Reference No</th>
                  <th className="p-3">Account No</th>
                  <th className="p-3">Opening Balance</th>
                  <th className="p-3">Amount</th>
                  <th className="p-3">Charges</th>
                  <th className="p-3">GST</th>
                  <th className="p-3">Closing Balance</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>

              <tbody>
                {displayedItems.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-6">
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  displayedItems.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      {/* Serial Number */}
                      <td className="p-3 text-center font-medium">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>

                      <td className="p-3 text-center">{item.created_on}</td>
                      <td className="p-3 text-center">{item.rrn || "NA"}</td>
                      <td className="p-3 text-center">{item.account_no}</td>
                      <td className="p-3 text-center">{item.before_bal}</td>
                      <td className="p-3 text-center">{item.amount}</td>
                      <td className="p-3 text-center">{item.charges}</td>
                      <td className="p-3 text-center">{item.gst}</td>
                      <td className="p-3 text-center">{item.after_bal}</td>

                      <td className="p-3 text-center">
                        <span
                          className={`px-3 py-1 rounded text-sm ${
                            item.status === "SUCCESS"
                              ? "bg-green-100 text-green-600"
                              : "bg-red-100 text-red-600"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onPageClick={handlePageClick}
        />

        {/* Settlement Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center montserrat-font-1 justify-center">
            <div className="bg-white p-6 rounded-lg montserrat-font-1 w-[400px]">
              <h3 className="font-semibold text-lg mb-4">Settlement</h3>

              {bankInfo.map((bank, i) => (
                <div key={i} className="mb-4 text-sm">
                  <p>Account : {bank.us_account}</p>
                  <p>IFSC : {bank.ifsc_code}</p>
                  <p>Bank : {bank.bank_name}</p>
                </div>
              ))}

              <input
                type="number"
                placeholder="Enter Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="border w-full px-3 py-2 rounded mb-4"
              />

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSettlement}
                  className="px-4 py-2 bg-green-500 text-white rounded"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PayOut;
